import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  marginBottom: '20px',
}));

const StepTitle = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '10px',
}));

const StepContent = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
  marginBottom: '15px',
}));

const HowToEncryptImage: React.FC = () => {
  const steps = [
    {
      title: 'Step 1: Prepare Your Message',
      content: 'Write the secret message you want to protect. This can be any text data - passwords, notes, confidential information, etc.'
    },
    {
      title: 'Step 2: Choose a Strong Password',
      content: 'Create a strong password for encryption. Use a mix of uppercase, lowercase, numbers, and special characters. The password is used for AES-256-GCM encryption.'
    },
    {
      title: 'Step 3: Select an Image',
      content: 'Upload an image (PNG, BMP, or JPEG) that will serve as the carrier. The image should be high quality and have enough pixels to hold your data.'
    },
    {
      title: 'Step 4: Set Encoding Parameters',
      content: 'Choose the bit depth for encoding: 1-bit (least visible, smallest capacity), 2-bit (balanced), or 4-bit (most capacity, slightly more visible).'
    },
    {
      title: 'Step 5: Encrypt and Embed',
      content: 'Click the Encode button. Your message is first encrypted with AES-256-GCM, then embedded into the image using LSB steganography.'
    },
    {
      title: 'Step 6: Download and Verify',
      content: 'Download the encrypted image. You can verify it works by using the Decode tab with the same password to extract your message.'
    }
  ];

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <SectionTitle>How to Encrypt an Image</SectionTitle>
        
        <ContentCard>
          <CardContent>
            <Stepper orientation="vertical">
              {steps.map((step, index) => (
                <Step key={index} active>
                  <StepLabel>
                    <StepTitle>{step.title}</StepTitle>
                  </StepLabel>
                  <StepContent>{step.content}</StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </ContentCard>

        <ContentCard>
          <CardContent>
            <StepTitle>Security Features</StepTitle>
            <StepContent>
              • AES-256-GCM encryption (military-grade)<br />
              • PBKDF2 key derivation for password protection<br />
              • Dual-layer security (encryption + steganography)<br />
              • No data stored on servers<br />
              • Client-side processing only
            </StepContent>
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default HowToEncryptImage;
